import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '../../api/useTranslation';
import { GtagLogger } from '../../logger/GtagLogger';
import { Logger } from '../../logger/Logger';
import { SessionStorage } from '../../storage/session/SessionStorage';
import { useConfigSlice } from '../../store/slices/config/selectors';

const logPerformanceData = (): void => {
  let performanceEntries = performance
    .getEntriesByType('resource')
    .filter((log) => (log as PerformanceResourceTiming).initiatorType == 'script' && log.name.endsWith('/bundle.js'));

  performanceEntries.forEach((entry) => {
    const size = (entry as PerformanceResourceTiming).transferSize;
    Logger.updateBandwidth(Math.round((size / entry.duration) * 1000));
  });

  performanceEntries = performance
    .getEntriesByType('resource')
    .filter((log) => (log as PerformanceResourceTiming).initiatorType == 'fetch');

  let perfMeasured: boolean = false;
  performanceEntries.forEach((entry) => {
    const metricName = entry.name.match(/\/([\w.]+)\?/)?.[1];

    if (metricName) {
      Logger.logDuration('performance', metricName, Date.now(), Math.round(entry.duration), '', false);
    }
    if (!perfMeasured && entry.name.indexOf('/bundle.')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const transferSize = entry.transferSize;
      const isFromCache = transferSize <= 500;
      Logger.logEvent('loaded_from_cache', {
        loggerName: 'performance',
        additional: '' + isFromCache,
        bandwidth: transferSize,
      });
      perfMeasured = true;
    }
  });

  Logger.logDuration('performance', 'ready', Date.now(), Math.round(performance.now()), '', false);

  const loadingStartContext = {
    standalone: window.parent === window,
    initial: !SessionStorage.get('previousSession'),
  };
  Logger.logEvent('ready', {
    additional: `${loadingStartContext.initial ? 'initial' : 'refresh'}_${
      loadingStartContext.standalone ? 'standalone' : 'iframe'
    }`,
    instant: true,
  });
  if (window.navigator.hardwareConcurrency) {
    Logger.logEvent('system_core_count', {
      additional: '' + window.navigator.hardwareConcurrency,
    });
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const connection = window.navigator.connection;
  if (connection) {
    if (connection.effectiveType) {
      Logger.logEvent('system_connection_type', {
        additional: connection.effectiveType,
      });
    }
    if (connection.rtt) {
      Logger.logEvent('system_connection_rtt', {
        additional: connection.rtt,
      });
    }
  }
  const loadingTimeForGtag = performance.measure('loadingTimeForGtag', 'loadStartForGtag');
  if (loadingTimeForGtag?.duration) {
    GtagLogger.logEvent('game-loaded', loadingTimeForGtag.duration / 1000);
  } else {
    GtagLogger.logEvent('game-loaded', 0);
  }
};

/**
 * Hook that handles curtain disappearing when game is loaded
 * @returns boolean value, that represents if the curtain disappearing animation has finished
 */
export const useCurtain = (progress: number): boolean => {
  const session = useConfigSlice.session();
  const curtain = useRef<HTMLDivElement>(document.querySelector<HTMLDivElement>('.curtain'));
  const showJurMsg = useConfigSlice.casinoConfig()?.showAlderneyMsg;
  const { translations } = useTranslation();
  const [curtainVisible, setCurtainVisible] = useState<boolean>(true);

  useEffect(() => {
    const curtainElement = curtain.current;
    if (curtainElement) {
      const progressBar = curtainElement.querySelector<HTMLProgressElement>('progress');

      // get access to curtain footer
      const curtainFooter = curtainElement.querySelector<HTMLDivElement>('.curtain-footer');
      if (
        curtainFooter &&
        showJurMsg &&
        translations['GAMBLING_COMMISSION_TITLE'] &&
        translations['AGCC_REGULATORY_MESSAGE']
      ) {
        // curtain footer header and message accordingly
        curtainFooter.childNodes[0].textContent = translations['GAMBLING_COMMISSION_TITLE'];
        curtainFooter.childNodes[1].textContent = translations['AGCC_REGULATORY_MESSAGE'];
        curtainFooter.classList.add('visible');
      } else {
        curtainFooter?.classList.remove('visible');
      }

      if (!progressBar) return;
      progressBar.value = progress;
      if (progress === 100 || session === null) {
        logPerformanceData();
        curtainElement.classList.add('curtain_lifted');
        const animationEndHandler = (): void => {
          if (curtainElement) {
            curtainElement.classList.add('curtain_dead');
            curtainElement.classList.remove('curtain_lifted');
            setCurtainVisible(false);
          }
        };
        curtainElement.addEventListener('animationend', animationEndHandler);
        return (): void => {
          curtainElement.removeEventListener('animationend', animationEndHandler);
        };
      }
    }
  }, [progress, session, showJurMsg, translations]);

  return curtainVisible;
};
